import { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  InlineNotification,
  MenuButton,
  MenuItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import routes from '~/routes';
import {
  crmCreatePermissionLoadableAtom,
  tractEnumLoadableAtom,
} from './atoms';
import CrmTractsContent from './crm-tracts-content';
import {
  BULK_UPLOADS_SEARCH,
  BulkUploadsTable,
} from './bulk-upload/bulk-uploads-table';

/**
 * CRM Tract Library page. Renders PageHeader with the "Create CRM Asset" menu
 * (one asset through the wizard, or many through bulk upload), then two tabs:
 * the tracts list, which stays mounted, and the Bulk uploads list, mounted
 * only while its tab is open so it stops polling when left.
 * @return {JSX.Element} The CRM Tract Library page.
 */
const CrmTractsPage: FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const [tabIndex, setTabIndex] = useState(
    search === BULK_UPLOADS_SEARCH ? 1 : 0
  );
  // The query follows the tab, so a reload or Back lands on the same list.
  const selectTab = (index: number) => {
    setTabIndex(index);
    history.replace({ search: index === 1 ? BULK_UPLOADS_SEARCH : '' });
  };

  const tractEnums = useAtomValue(tractEnumLoadableAtom);
  const crmCreatePermission = useAtomValue(crmCreatePermissionLoadableAtom);

  const hasCrmCreatePermission =
    crmCreatePermission.state === 'hasData' &&
    crmCreatePermission.data?.hasPermissions;

  const userEmail =
    crmCreatePermission.state === 'hasData' && crmCreatePermission.data?.email;

  const isEnumsError =
    tractEnums.state === 'hasError' ||
    (tractEnums.state === 'hasData' &&
      tractEnums.data &&
      'error' in tractEnums.data);
  const isEnumsLoading = tractEnums.state === 'loading';

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="CRM"
        actions={
          <MenuButton
            label="Create CRM Asset"
            kind="primary"
            size="lg"
            menuAlignment="bottom-end"
          >
            <MenuItem
              label="Single Asset"
              disabled={
                isEnumsError || isEnumsLoading || !hasCrmCreatePermission
              }
              onClick={() => history.push(routes.crmTractWizardNewPath)}
            />
            <MenuItem
              label="Bulk Upload Assets"
              disabled={!hasCrmCreatePermission}
              onClick={() => history.push(routes.crmBulkUploadNewPath)}
            />
          </MenuButton>
        }
      />

      {crmCreatePermission.state === 'hasError' && (
        <InlineNotification
          kind="error"
          title="Failed to load CRM create permission"
          subtitle="Unable to load CRM create permission. Please refresh the page or contact support if the issue persists."
        />
      )}

      {!hasCrmCreatePermission && crmCreatePermission.state === 'hasData' && (
        <InlineNotification
          kind="info"
          title={`You ${
            userEmail ? `(${userEmail})` : ''
          } do not have permission to create CRM assets, one at a time or in bulk.`}
          subtitle="Please reach out to #salesforce-users Slack channel."
        />
      )}

      {isEnumsError && (
        <InlineNotification
          kind="error"
          title="Failed to load enum data"
          subtitle="Unable to load required enum data. Please refresh the page or contact support if the issue persists."
        />
      )}

      <Tabs
        selectedIndex={tabIndex}
        onChange={({ selectedIndex }) => selectTab(selectedIndex)}
      >
        <TabList aria-label="CRM sections" contained>
          <Tab>Tracts</Tab>
          <Tab>Bulk uploads</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <CrmTractsContent />
          </TabPanel>
          <TabPanel>{tabIndex === 1 && <BulkUploadsTable />}</TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};

export default CrmTractsPage;
